import React, {
  ReactNode,
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AppHomeButton } from "@pedagogue/ui-core";
import {
  ChevronLeft,
  ChevronRight,
  Layout,
  Users,
  BarChart2,
  LogOut,
  Image,
  Shield,
  Home,
  Building2,
  Check,
  ChevronsUpDown,
  Sparkles,
  Menu,
  X,
} from "lucide-react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
} from "@headlessui/react";
import Typography from "../components/common/Typography";
import LogoImg from "../images/LogoImg";
import { useAuth } from "../supabase/AuthProvider";
import { useUserProfile } from "../context/UserProfileContext";
import { Feature, useEntitlements } from "../context/EntitlementsContext";
import { useOrgSwitcher } from "../hooks/useOrgSwitcher";
import Avatar from "../components/common/Avatar";
import Button from "../components/common/Button";

interface OrgOption {
  id: string;
  name: string;
}

interface OrgSwitcherComboboxProps {
  organisations: OrgOption[];
  selectedOrgId: string | null;
  onSelect: (orgId: string) => void;
}

const OrgSwitcherCombobox: React.FC<OrgSwitcherComboboxProps> = ({
  organisations,
  selectedOrgId,
  onSelect,
}) => {
  const [query, setQuery] = useState("");

  const selectedOrg = useMemo(
    () => organisations.find((o) => o.id === selectedOrgId) || null,
    [organisations, selectedOrgId]
  );

  const filteredOrgs = useMemo(
    () =>
      query === ""
        ? organisations
        : organisations.filter((o) =>
            o.name.toLowerCase().includes(query.toLowerCase())
          ),
    [organisations, query]
  );

  return (
    <div className="px-4 pb-2" data-testid="org-switcher">
      <label className="block text-xs font-medium text-gray-500 mb-1">
        <Building2 size={12} className="inline mr-1" />
        Organisation
      </label>
      <Combobox
        value={selectedOrg}
        onChange={(org: OrgOption | null) => {
          if (org) onSelect(org.id);
        }}
        by="id"
      >
        <div className="relative">
          <div className="relative w-full cursor-default overflow-hidden rounded-lg border border-gray-300 bg-white text-left focus-within:ring-2 focus-within:ring-blue-500">
            <ComboboxInput
              className="w-full border-none py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:outline-none"
              displayValue={(org: OrgOption | null) => org?.name || ""}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search organisations..."
            />
            <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronsUpDown size={16} className="text-gray-400" />
            </ComboboxButton>
          </div>
          <ComboboxOptions className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none">
            {filteredOrgs.length === 0 && query !== "" ? (
              <div className="relative cursor-default select-none px-4 py-2 text-gray-500">
                No organisations found.
              </div>
            ) : (
              filteredOrgs.map((org) => (
                <ComboboxOption
                  key={org.id}
                  value={org}
                  className="group relative cursor-pointer select-none py-2 pl-10 pr-4 text-gray-900 data-[focus]:bg-blue-50 data-[focus]:text-blue-900"
                >
                  <span className="block truncate font-normal group-data-[selected]:font-semibold">
                    {org.name}
                  </span>
                  <span className="absolute inset-y-0 left-0 hidden items-center pl-3 text-blue-600 group-data-[selected]:flex">
                    <Check size={16} />
                  </span>
                </ComboboxOption>
              ))
            )}
          </ComboboxOptions>
        </div>
      </Combobox>
      <p className="mt-1 text-xs text-gray-400">
        Read-only access to non-home organisations
      </p>
    </div>
  );
};

interface DashboardLayoutProps {
  children: ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(() => window.innerWidth < 768);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState<number>(280);
  const draggingRef = useRef<boolean>(false);
  const startXRef = useRef<number>(0);
  const minSidebarWidth = 80;
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { userProfile } = useUserProfile();
  // WS-P: hide the Analytics nav from an SLT-pilot org admin (ADVANCED_ANALYTICS
  // denied); Whole-School + super-admin keep it.
  const { hasFeature } = useEntitlements();
  const canViewAnalytics = hasFeature(Feature.ADVANCED_ANALYTICS);
  // Play signals nav is entitlement-gated (deny-by-default; pilot orgs via
  // override, INTERNAL tier true). The /play page is home-org-only by design:
  // the backend APIs take no org parameter, so /play must present NO
  // org-selection affordance — the switcher and the selected-org breadcrumb
  // are suppressed on it (the breadcrumb is already unreachable there via
  // useOrgSwitcher's URL fallback; the guard makes the contract explicit).
  const canViewWorkbooks = hasFeature(Feature.WORKBOOKS) && hasFeature(Feature.ADMIN_CONSOLE);
  const canViewPlay = hasFeature(Feature.PLAY_SANDBOX);
  const isPlayRoute = location.pathname.startsWith("/play");
  const {
    organisations,
    selectedOrgId,
    selectedOrgName,
    isViewingHomeOrg,
    isSuperAdmin,
    groups = [],
  } = useOrgSwitcher();

  // Function to get current module name and icon based on location
  const getCurrentModuleInfo = useCallback(() => {
    const currentPath = location.pathname;

    if (currentPath === "/") return { name: "Dashboard", icon: Layout };
    if (currentPath.startsWith("/organisation/"))
      return { name: "Users Management", icon: Users };
    if (currentPath.startsWith("/analytics/"))
      return { name: "Analytics", icon: BarChart2 };
    if (currentPath.startsWith("/groups/")) return { name: "Group overview", icon: BarChart2 };
    if (currentPath.startsWith("/workbooks/")) return {name:"Workbooks",icon:BarChart2};
    if (currentPath.startsWith("/branding/"))
      return { name: "Branding", icon: Image };
    if (currentPath.startsWith("/play"))
      return { name: "Play", icon: Sparkles };
    if (currentPath.startsWith("/super-admin"))
      return { name: "Super Admin", icon: Shield };
    return { name: "Dashboard", icon: Layout }; // Default fallback
  }, [location.pathname]);

  useEffect(() => {
    const moduleInfo = getCurrentModuleInfo();
    const baseTitle = "Pedagogue Org Admin";
    if (moduleInfo?.name) {
      document.title = `${baseTitle} | ${moduleInfo.name}`;
    } else {
      document.title = baseTitle;
    }
  }, [getCurrentModuleInfo]);

  // Build menu based on user role
  const buildSidebarMenu = () => {
    const baseMenu = [{ id: 1, name: "Dashboard", to: "/", icon: Layout }];

    // Add Super Admin specific menu items
    if (userProfile?.role === "super_admin") {
      const orgId = selectedOrgId || userProfile.organisation_id;
      const menu = [
        {
          id: 1,
          name: "Super Admin",
          to: "/super-admin",
          icon: Shield,
        },
      ];

      if (orgId) {
        menu.push(
          {
            id: 2,
            name: "Dashboard",
            to: "/",
            icon: Layout,
          },
          {
            id: 3,
            name: "Users Management",
            to: `/organisation/${orgId}`,
            icon: Users,
          },
          {
            id: 4,
            name: "Analytics",
            to: `/analytics/dashboard/${orgId}`,
            icon: BarChart2,
          },
          {
            id: 5,
            name: "Branding",
            to: `/branding/${orgId}`,
            icon: Image,
          }
        );
      }

      if (orgId && canViewWorkbooks) menu.push({id:7,name:"Workbooks",to:`/workbooks/${orgId}`,icon:BarChart2});
      if (canViewPlay) {
        // Home-org-only: /play always shows the viewer's own organisation
        // (the backend APIs take no org parameter), so no orgId in the path.
        menu.push({ id: 6, name: "Play", to: "/play", icon: Sparkles });
      }

      return menu;
    }

    // Add Admin specific menu items
    const adminEquivalentRoles = ["admin", "organisation_admin", "platform_admin", "billing_admin"];
    if (userProfile?.role && adminEquivalentRoles.includes(userProfile.role)) {
      return [
        ...baseMenu,
        {
          id: 2,
          name: "Users Management",
          to: `/organisation/${userProfile?.organisation_id || ""}`,
          icon: Users,
        },
        ...(canViewWorkbooks && ["admin","organisation_admin"].includes(userProfile.role) ? [{id:7,name:"Workbooks",to:`/workbooks/${userProfile.organisation_id}`,icon:BarChart2}] : []),
        // WS-P: Analytics hidden for SLT-pilot orgs (Learn-app only).
        ...(canViewAnalytics
          ? [
              {
                id: 3,
                name: "Analytics",
                to: `/analytics/dashboard/${userProfile?.organisation_id || ""}`,
                icon: BarChart2,
              },
            ]
          : []),
        {
          id: 4,
          name: "Branding",
          to: `/branding/${userProfile?.organisation_id || ""}`,
          icon: Image,
        },
        ...(canViewPlay
          ? [
              {
                id: 5,
                name: "Play",
                to: "/play",
                icon: Sparkles,
              },
            ]
          : []),
      ];
    }

    // Default menu for other roles
    return [
      ...baseMenu,
    ];
  };

  const isMemberSchool = !isSuperAdmin && !isViewingHomeOrg && groups.some(group => group.schools.some(school => school.id === selectedOrgId));
  const navGroup = groups.find(group => group.id === location.pathname.match(/^\/groups\/([^/]+)/)?.[1]) ?? groups[0];
  const sidebarMenu = [
    ...(isMemberSchool ? [{ id: 3, name: "Analytics", to: `/analytics/dashboard/${selectedOrgId}`, icon: BarChart2 }] : buildSidebarMenu()),
    ...(navGroup ? [{ id: 100, name: "Group overview", to: `/groups/${navGroup.id}/overview`, icon: BarChart2 }] : []),
  ];

  // Function to start dragging when mouse is pressed
  const handleMouseDown = (event: React.MouseEvent) => {
    if (isCollapsed) return;
    event.preventDefault();
    draggingRef.current = true;
    startXRef.current = event.clientX;
    document.body.style.cursor = "col-resize";
  };

  // Function to track mouse movement while dragging
  const handleMouseMove = useCallback((event: MouseEvent) => {
    if (!draggingRef.current || isCollapsed) return;
    const deltaX = event.clientX - startXRef.current;
    setSidebarWidth((prevWidth) =>
      Math.max(220, Math.min(400, prevWidth + deltaX))
    );
    startXRef.current = event.clientX;
  }, [isCollapsed]);

  useEffect(() => {
    const fitNarrowViewport = () => {
      if (window.innerWidth < 768) setIsCollapsed(true);
    };
    window.addEventListener("resize", fitNarrowViewport);
    return () => window.removeEventListener("resize", fitNarrowViewport);
  }, []);

  // Function to stop dragging
  const handleMouseUp = useCallback(() => {
    draggingRef.current = false;
    document.body.style.cursor = "default";
  }, []);

  // Attach event listeners on mount and cleanup on unmount
  useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  // Toggle Sidebar Collapse
  const toggleCollapse = () => {
    setIsCollapsed((prev) => !prev);
    setSidebarWidth(isCollapsed ? 280 : minSidebarWidth);
  };

  const APP_LOGIN_URL = process.env.REACT_APP_APP_HOME_URL
    ? `${process.env.REACT_APP_APP_HOME_URL}/login`
    : "https://app.pedagogue.io/login";

  const handleSignOut = async () => {
    try {
      await signOut();
      window.location.href = APP_LOGIN_URL;
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1025px)");
    const closeOnDesktop = () => { if (desktop.matches) setMobileOpen(false); };
    desktop.addEventListener("change", closeOnDesktop);
    return () => desktop.removeEventListener("change", closeOnDesktop);
  }, []);

  const renderSidebar = (collapsed: boolean, mobile = false) => (
      <div
        className={`shrink-0 h-full bg-white shadow-lg text-textPrimary max-h-svh flex flex-col transition-all duration-300 border-r border-gray-200 ${
          collapsed ? "w-20" : ""
        }`}
        style={{ width: mobile ? "100%" : collapsed ? minSidebarWidth : sidebarWidth }}
      >
        {/* Logo and Brand with Collapse Button */}
        <div
          className={`flex items-center ${
            collapsed ? "justify-center" : "justify-between"
          } relative py-4 px-4`}
        >
          <div className="flex items-center space-x-2">
            <span
              className={`text-primary font-bold text-2xl text-center ${collapsed ? "block" : "hidden"}`}
            >
              P
            </span>
            {!collapsed && <LogoImg />}
          </div>
          <button
            aria-label={mobile ? "Close navigation" : collapsed ? "Expand sidebar" : "Collapse sidebar"}
            onClick={mobile ? () => setMobileOpen(false) : toggleCollapse}
            className={`${mobile ? "text-textPrimary" : "text-border"} hover:text-secondary transition-colors duration-200 p-1 rounded-md hover:bg-gray-100 ${
              collapsed ? "absolute right-0 top-4" : ""
            }`}
          >
            {mobile ? <X size={20} /> : collapsed ? (
              <ChevronRight size={20} />
            ) : (
              <ChevronLeft size={20} />
            )}
          </button>
        </div>
        {/* Org Switcher (Super Admin only; never on /play — home-org-only) */}
        {(isSuperAdmin || groups.length > 0) && organisations.length > 1 && !collapsed && !isPlayRoute && (
          <OrgSwitcherCombobox
            organisations={organisations}
            selectedOrgId={selectedOrgId}
            onSelect={(orgId) => navigate(!isSuperAdmin || location.pathname.startsWith("/analytics/") || location.pathname.startsWith("/groups/") ? `/analytics/dashboard/${orgId}` : location.pathname.startsWith("/workbooks/") ? `/workbooks/${orgId}` : `/organisation/${orgId}`)}
          />
        )}
        {groups.length > 1 && !collapsed ? <label className="mx-4 mb-4 block text-xs text-pd-slate">Group overview<select aria-label="Choose a group" className="mt-1 w-full rounded border border-pd-rule bg-pd-white px-2 py-2 text-pd-ink" value={location.pathname.match(/^\/groups\/([^/]+)/)?.[1] ?? ""} onChange={event => { if (event.target.value) navigate(`/groups/${event.target.value}/overview`); }}><option value="">Choose a group</option>{groups.map(group => <option key={group.id} value={group.id}>{group.name}</option>)}</select></label> : null}
        {/* Navigation */}
        <nav className="flex-1 flex flex-col p-4 pt-0 space-y-2 overflow-y-auto">
          {sidebarMenu.map((menu) => {
            const MenuIcon = menu.icon;
            const isActive = (() => {
              // Handle exact matches for static routes
              if (menu.to === "/" || menu.to === "/home") {
                return (
                  location.pathname === "/" || location.pathname === "/home"
                );
              }
              // Handle dynamic routes
              if (menu.to.includes("/organisation/")) {
                return (
                  location.pathname.startsWith("/organisation/") &&
                  !location.pathname.includes("/branding")
                );
              }
              if (menu.to.includes("/branding/")) {
                return location.pathname.startsWith("/branding/");
              }
              if (menu.to.includes("/analytics/dashboard/")) {
                return location.pathname.startsWith("/analytics/dashboard/");
              }
              if (menu.to.includes("/super-admin/")) {
                return location.pathname.startsWith("/super-admin/");
              }
              return location.pathname === menu.to;
            })();

            return (
              <Link
                key={menu.id}
                to={menu.to}
                onClick={() => setMobileOpen(false)}
                aria-label={menu.name}
                className={`px-4 py-3 flex items-center ${
                  collapsed ? "justify-center" : "space-x-3"
                } rounded-lg transition-colors duration-200 ${
                  isActive
                    ? "bg-stone-50 text-success/90"
                    : "text-textPrimary hover:bg-gray-100"
                }`}
              >
                <MenuIcon size={20} />
                {!collapsed && (
                  <Typography variant="h6" color="none">
                    {menu.name}
                  </Typography>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Profile Section */}
        {user && (
          <div className="border-t border-gray-200 p-4">
            <div className="flex items-center space-x-3 mb-4">
              <Avatar
                name={userProfile?.full_name || userProfile?.name || user.email || "User"}
                size="md"
                src={userProfile?.avatar_url}
              />
              {!collapsed && (
                <div className="min-w-0 break-words">
                  <Typography variant="h6" className="font-medium">
                    {userProfile?.full_name || userProfile?.name || "User"}
                  </Typography>
                  <Typography variant="small" className="text-slate-400">
                    {userProfile?.email || user?.email || ""}
                  </Typography>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Button
                variant="filled"
                onClick={handleSignOut}
                className={`w-full ${collapsed ? "!gap-0" : ""} rounded-lg transition-colors duration-200 hover:opacity-80`}
                startIcon={<LogOut size={20} />}
              >
                {!collapsed && (
                  <Typography variant="h6" color="none">
                    Logout
                  </Typography>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
  );

  return (
    <div className="flex relative h-dvh min-w-0 overflow-hidden">
      <div className="hidden lg:block shrink-0">{renderSidebar(isCollapsed)}</div>
      <Dialog open={mobileOpen} onClose={setMobileOpen} className="fixed inset-0 z-[10000] lg:hidden">
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex">
          <DialogPanel className="h-full w-80 max-w-[calc(100vw-3rem)]">
            <DialogTitle className="sr-only">Admin navigation</DialogTitle>
            {renderSidebar(false, true)}
          </DialogPanel>
        </div>
      </Dialog>

      {/* Resizable Handle */}
      {!isCollapsed && (
        <div
          className="hidden lg:block absolute top-0 bottom-0 cursor-col-resize z-40"
          style={{ left: `${sidebarWidth}px`, width: "5px" }}
          onMouseDown={handleMouseDown}
        />
      )}

      <div className="flex-1 flex flex-col min-h-0 min-w-0">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200 py-4 px-3 sm:px-6 flex-shrink-0">
          <div className="flex items-start gap-2">
            <button type="button" aria-label="Open navigation" aria-expanded={mobileOpen}
              onClick={() => setMobileOpen(true)}
              className="lg:hidden shrink-0 p-2 rounded-lg hover:bg-gray-100">
              <Menu size={20} />
            </button>
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              {/* App Home: always-visible launchpad-return control
                  (APP_LAUNCHPAD_UX_SPEC §7.1 — the shared component defaults the
                  URL, so it no longer disappears when REACT_APP_APP_HOME_URL is
                  unset). Uses the shared DEFAULT grid/launchpad glyph + matching
                  rounded hover, so it is UNIFORM with Learner/Super-Admin (no
                  per-app icon override). */}
              <AppHomeButton
                url={process.env.REACT_APP_APP_HOME_URL}
                className="rounded-lg hover:bg-gray-100 transition-colors mr-3"
              />
              <ChevronRight size={16} className="text-gray-300 mr-2" />
              {/* Breadcrumb Navigation */}
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <div className="flex items-center space-x-2 text-gray-500">
                  <Home size={16} className="text-gray-400" />
                  <Typography
                    variant="h6"
                    weight="normal"
                    className="!text-gray-500 !text-sm"
                  >
                    Admin
                  </Typography>
                </div>
                {isSuperAdmin && !isViewingHomeOrg && selectedOrgName && !isPlayRoute && (
                  <>
                    <ChevronRight size={16} className="text-gray-300" />
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <Building2 size={16} className="text-amber-500" />
                      <Typography
                        variant="h6"
                        weight="normal"
                        className="!text-gray-500 !text-sm break-all"
                      >
                        {selectedOrgName}
                      </Typography>
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800"
                        data-testid="read-only-badge"
                      >
                        Read-Only
                      </span>
                    </div>
                  </>
                )}
                <ChevronRight size={16} className="text-gray-300" />
                <div className="flex items-center space-x-2">
                  {(() => {
                    const moduleInfo = getCurrentModuleInfo();
                    const IconComponent = moduleInfo.icon;
                    return (
                      <>
                        <IconComponent size={16} className="text-primary" />
                        <Typography
                          variant="h6"
                          weight="normal"
                          className="!text-gray-500 !text-sm"
                        >
                          {moduleInfo.name}
                        </Typography>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 min-h-0 min-w-0 p-3 sm:p-6 overflow-auto">
          <div className="w-full">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
